// Bistable silicone domes on a soft pad (push-pop). A pressed dome first gives
// a little, then snaps through past its new side and rings down. The snap is a
// threshold state flip, as in graphics-atlas' bond-breaking destruction model;
// the ring-down is the critically damped inertialization residual from its
// motion-transition model. All values are art direction, not measurements.
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
// Closed-form critically damped residual: x(t) = (x0 + (v0 + k x0) t) e^(-k t).
export const inertialResidual = (x0, v0, k, t) => (x0 + (v0 + k * x0) * t) * Math.exp(-k * t);

export class PopDomes {
  constructor(domes, { ring = 18, snapSpeed = 55, give = .55, stagger = .045 } = {}) {
    // domes: [{center:[x,y,z] at the cap base, radius, axis}] in toy-local space.
    this.domes = domes.map(d => ({ center: [...d.center], radius: d.radius, axis: [...(d.axis ?? [0, 1, 0])] }));
    this.count = this.domes.length;
    this.state = new Int8Array(this.count).fill(1);     // +1 raised, -1 pushed through
    this.amount = new Float64Array(this.count).fill(1); // rendered pose: cap height scale
    this.snapAge = new Float64Array(this.count).fill(Infinity);
    this.snapFrom = new Float64Array(this.count);
    Object.assign(this, { ring, snapSpeed, give, stagger });
    this.pressed = -1; this.push = 0; this.auto = null; this.queue = []; this.clock = 0;
    this.activity = 0; this.snaps = 0; this.onSnap = null;
  }
  get downCount() { let n = 0; for (const s of this.state) if (s < 0) n++; return n; }
  // A finger lands on dome i. Only a raised dome can be pushed from above.
  grab(index) { this.pressed = Number.isInteger(index) && index >= 0 && index < this.count ? index : -1; this.push = 0; this.auto = null; }
  // amount: 0 untouched, 1 or more snaps the pressed dome through.
  press(amount) { if (this.pressed >= 0 && Number.isFinite(amount)) this.push = Math.max(0, amount); }
  release() { if (!this.auto) { this.pressed = -1; this.push = 0; } }
  flip(i) {
    this.snapFrom[i] = this.amount[i]; this.state[i] = -this.state[i]; this.snapAge[i] = 0; this.snaps++;
    this.onSnap?.(i, this.state[i]);
  }
  // Turning the pad over swaps every dome, as a quick wave from left to right.
  flipAll() {
    const order = [...this.domes.keys()].sort((a, b) => this.domes[a].center[0] - this.domes[b].center[0]);
    this.queue = order.map((index, k) => ({ at: this.clock + k * this.stagger, index }));
    return true;
  }
  // Keyboard demo: press the next raised dome, or turn the pad over when none is left.
  demo() {
    const order = [...this.domes.keys()].sort((a, b) => this.domes[b].center[2] - this.domes[a].center[2] || this.domes[a].center[0] - this.domes[b].center[0]);
    const index = order.find(i => this.state[i] > 0);
    if (index === undefined) return this.flipAll();
    this.pressed = index; this.push = 0; this.auto = { time: 0 }; return true;
  }
  reset() {
    this.state.fill(1); this.amount.fill(1); this.snapAge.fill(Infinity); this.snapFrom.fill(0);
    this.pressed = -1; this.push = 0; this.auto = null; this.queue = []; this.activity = 0; this.snaps = 0;
  }
  step(dt) {
    if (!Number.isFinite(dt) || dt <= 0) return;
    dt = Math.min(dt, 1 / 30); this.clock += dt;
    while (this.queue.length && this.queue[0].at <= this.clock) this.flip(this.queue.shift().index);
    if (this.auto) {
      this.auto.time += dt; this.push = clamp(this.auto.time / .3, 0, 1.2);
      if (this.auto.time > .6) { this.auto = null; this.pressed = -1; this.push = 0; }
    }
    let activity = 0;
    for (let i = 0; i < this.count; i++) {
      let target = this.state[i];
      if (i === this.pressed && this.state[i] > 0) {
        const depth = clamp(this.push, 0, 1.2);
        if (depth >= 1) { this.flip(i); target = this.state[i]; }
        else target = this.state[i] * (1 - this.give * depth);
      }
      let value;
      if (this.snapAge[i] < 1.5) {
        this.snapAge[i] += dt;
        const x0 = this.snapFrom[i] - this.state[i], v0 = this.state[i] * this.snapSpeed * Math.abs(x0) / 1.5;
        const residual = inertialResidual(x0, v0, this.ring, this.snapAge[i]);
        value = this.state[i] + residual;
        if (this.snapAge[i] > .25 && Math.abs(residual) < .002) { this.snapAge[i] = Infinity; value = this.state[i]; }
      } else {
        const previous = this.amount[i];
        value = previous + (target - previous) * (1 - Math.exp(-dt / .05));
        if (Math.abs(value - target) < .001) value = target;
      }
      activity = Math.max(activity, Math.abs(value - this.amount[i]) / dt);
      this.amount[i] = value;
    }
    if (this.pressed >= 0 || this.auto || this.queue.length) activity = Math.max(activity, .001);
    this.activity = activity < 1e-4 ? 0 : activity;
  }
  // Pose a cap vertex: its height above the base scales with the dome amount, so
  // -1 mirrors the cap through its base. The normal of a surface scaled along the
  // axis keeps its tangential part and divides its axial part by the scale.
  deform(i, rest, out, normal) {
    const d = this.domes[i], a = this.amount[i], axis = d.axis;
    const height = (rest[0] - d.center[0]) * axis[0] + (rest[1] - d.center[1]) * axis[1] + (rest[2] - d.center[2]) * axis[2];
    if (height <= 0 || a === 1) return;
    const lift = height * (a - 1);
    out[0] += axis[0] * lift; out[1] += axis[1] * lift; out[2] += axis[2] * lift;
    if (!normal) return;
    const scale = Math.abs(a) < .15 ? (a < 0 ? -.15 : .15) : a, sign = scale < 0 ? -1 : 1;
    const along = normal[0] * axis[0] + normal[1] * axis[1] + normal[2] * axis[2];
    const nx = (normal[0] - along * axis[0] + along / scale * axis[0]) * sign;
    const ny = (normal[1] - along * axis[1] + along / scale * axis[1]) * sign;
    const nz = (normal[2] - along * axis[2] + along / scale * axis[2]) * sign;
    const length = Math.hypot(nx, ny, nz) || 1;
    normal[0] = nx / length; normal[1] = ny / length; normal[2] = nz / length;
  }
}
